import { useMutation } from '@tanstack/react-query';

import { fetchResidents } from '@/adapters/fetchExaCare';
import { queryClient } from '@/adapters/query';
import { invalidateCloseConnectionsQuery } from '@/components/ResidentFacesheet/hooks/useResidentCloseConnectionsQuery';
import { invalidateCallsQueries } from '@/pages/CRM/hooks/useCrmCallsQuery';
import { invalidateConversationQueries } from '@/pages/CRM/hooks/useCrmConversationsQuery';
import { invalidatePhoneBookQuery } from '@/pages/CRM/hooks/useCrmPhoneBookQuery';
import { invalidateEmailAddressBookQuery } from '@/pages/CRM/hooks/useEmailAddressBookQuery';
import { ResidentTagPayload } from '@/pages/ResidentPage/components/useResidentTagsQuery';
import { Room } from '@/stores/residentFacesheetsAtom';

import { invalidateResidentsQueriesV2 } from './residentsv2/useResidentsQuery';

import { USE_FACILITIES_QUERY_KEY } from './useFacilitiesQuery';
import {
  invalidateProspectResidentsQueries,
  PROSPECT_RESIDENTS_FIND_ONE_QUERY_KEY
} from './useProspectResidents';
import {
  ResidentGroup,
  USE_RESIDENT_GROUPS_QUERY_KEY
} from './useResidentGroupsQuery';
import {
  FIND_ALL_RESIDENTS_QUERY_KEY,
  FIND_ONE_RESIDENT_QUERY_KEY
} from './useResidentQuery';
import { TimezonePayload } from './useTimezonesQuery';
import { UserPayload } from './useUserQuery';

export interface CohabitantPayload {
  first_name?: string;
  last_name?: string;
  s3_photo_key: string;
  crm_status: 'PROSPECT' | 'MOVED_IN';
  email: string;
  id: string;
  crm_resident_info?: {
    lost_deal: boolean;
  } | null;
}

export enum Religion {
  PROTESTANT = 'Protestant',
  CATHOLIC = 'Catholic',
  MORMON = 'Mormon',
  ORTHODOX = 'Orthodox',
  UNAFFILIATED = 'Unaffiliated',
  ATHEIST = 'Atheist',
  AGNOSTIC = 'Agnostic',
  JEWISH = 'Jewish',
  MUSLIM = 'Muslim',
  HINDU = 'Hindu',
  BUDDHIST = 'Buddhist',
  OTHER_CHRISTIAN = 'Other Christian',
  OTHER = 'Other'
}

export interface CloseConnectionPayload {
  id: string;
  first_name: string;
  last_name: string;
  chief_point_of_contact: boolean;
  relationship?: string;
}

export interface ResidentPayload {
  authorized_rep: string;
  care_level_id: string;
  creating_user: string;
  dob: string | null;
  email: string;
  facility_id: string;
  first_name: string;
  last_name: string;
  gender: string;
  hospital_id: string;
  id: string;
  is_dnr: boolean;
  is_elopement: boolean;
  is_hospice: boolean;
  is_veteran: boolean;
  is_veteran_spouse: boolean;
  marital_status: string;
  phone: string;
  nickname: string;
  race_id: string;
  room_id: string;
  room_product_id: string;
  resident_group_id: string | null;
  cohabitant_id: string | null;
  cohabitant: CohabitantPayload | null;
  s3_photo_key: string;
  avatar_url?: string;
  service_start_date: string;
  ssn: string;
  timezone?: TimezonePayload;
  religion?: Religion | null;
  place_of_birth?: string | null;
  previous_residence?: string | null;
  previous_occupation?: string | null;
  biography?: string | null;
  medical_history: string;
  physical_limitations: string;
  nursing_treatment: string;
  cognitive_status: string;
  diagnosis?: string;
  medications?: string;
  cognitive_conditions?: string;
  respiratory_conditions?: string;
  diet?: string;
  grooming?: string;
  mobility?: string;
  out_of_community?: boolean;
  integration?: string;
  resident_departures?: Array<{
    // This always returns the last departure object
    id: string;
    estimated_return_date: string;
    departure_date: string;
    resident_id: string;
    reason?: string;
  }>;
  resident_group?: ResidentGroup | null;
  crm_status: 'PROSPECT' | 'MOVED_IN';
  next_resident_id?: string | null;
  previous_resident_id?: string | null;
  date_archived?: string | null;
  resident_archive_info?: {
    archived_reason: string | null;
    archiver: UserPayload | null;
  };
  room?: Room | null;
  resident_tags?: ResidentTagPayload[] | null;
  close_connections?: CloseConnectionPayload[] | null;
  is_call_pref: boolean | null;
  is_sms_pref: boolean | null;
  is_email_pref: boolean | null;
  is_from_ehr: boolean | null;
  is_duplicate: boolean | null;
  physical_move_out_date: string | null;
}

export function invalidateResidentFacesheetQueries(residentId: string) {
  queryClient.invalidateQueries([FIND_ONE_RESIDENT_QUERY_KEY, { residentId }]);
  queryClient.invalidateQueries(USE_RESIDENT_GROUPS_QUERY_KEY);
  queryClient.invalidateQueries(USE_FACILITIES_QUERY_KEY);
  queryClient.invalidateQueries([FIND_ALL_RESIDENTS_QUERY_KEY]);
  invalidateProspectResidentsQueries();
  queryClient.invalidateQueries([
    PROSPECT_RESIDENTS_FIND_ONE_QUERY_KEY,
    residentId
  ]);
  invalidateCallsQueries();
  invalidateConversationQueries();
  invalidatePhoneBookQuery();
  invalidateEmailAddressBookQuery();
  invalidateCloseConnectionsQuery();
  invalidateResidentsQueriesV2();
}

export function useResidentFacesheetMutation() {
  return useMutation(
    (resident: Partial<ResidentPayload>) => {
      return fetchResidents.put(`/residents/${resident.id}`, resident);
    },
    {
      onSuccess: (data, variables) => {
        invalidateResidentFacesheetQueries(variables.id!);
      }
    }
  );
}
